import type { ApiResponse } from '@bcube/shared';
import { api } from '@/lib/api';

// Normalized response from backend - consistent structure for all endpoints
export interface CnpjaNormalizedCompany {
  taxId: string;
  name: string;
  alias?: string;
  metadata: Record<string, unknown>;
}

// Mapped search result from backend - stable frontend contract
export interface CnpjaSearchItem {
  taxId: string;
  name: string;
  alias?: string;
}

// Mapped company data from backend - stable frontend contract
export interface CnpjaMappedCompany {
  taxId: string;
  alias?: string;
  company: {
    name: string;
    nature: { id: number };
    simei: { since: string | null };
  };
  status?: { id: number; text: string };
  registrations: Array<{
    number: string;
    state?: string;
    statusDate?: string;
    status: { id: number; text: string };
    enabled: boolean;
    type: { id?: number; text: string };
  }>;
  address?: {
    city: string;
    state: string;
    street: string;
    district: string;
    number: string;
    zip: string;
    details: string | null;
  };
  phones: Array<{ type: string; area: string; number: string }>;
  emails: Array<{ address: string; ownership: string }>;
}

export const cnpjaApi = {
  // Search by exact CNPJ - returns normalized structure
  findByCnpj: async (cnpj: string): Promise<CnpjaNormalizedCompany | null> => {
    try {
      const { data } = await api.get<ApiResponse<CnpjaNormalizedCompany>>(
        `/integrations/cnpja/${cnpj.replace(/\D/g, '')}/normalized`,
      );
      return data.data;
    } catch {
      return null;
    }
  },

  // Search by company name (fuzzy) - returns mapped search items
  findByName: async (name: string): Promise<CnpjaSearchItem[]> => {
    try {
      const { data } = await api.get<ApiResponse<CnpjaSearchItem[]>>('/integrations/cnpja/search', {
        params: { name },
      });
      return data.data || [];
    } catch {
      return [];
    }
  },

  // Get full CNPJ data with registrations (for document validation)
  getFullData: async (cnpj: string): Promise<CnpjaMappedCompany | null> => {
    try {
      const { data } = await api.get<ApiResponse<CnpjaMappedCompany>>(
        `/integrations/cnpja/${cnpj.replace(/\D/g, '')}/registration`,
      );
      return data.data;
    } catch {
      return null;
    }
  },
};
